import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBarChart, FiUsers, FiTarget, FiTrendingUp, FiCompass, FiAward, FiCheckCircle, FiStar, FiDollarSign, FiClock } = FiIcons;

const MarketingAnalysis = ({ formData, updateFormData, setCurrentStep }) => {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState({
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: [],
    recommendations: []
  });

  const handleNext = () => {
    updateFormData({ marketingAnalysis: analysis });
    setCurrentStep(6);
    navigate('/complete-plan');
  };

  const handlePrevious = () => {
    setCurrentStep(4);
    navigate('/action-plan');
  };

  const generateSWOTAnalysis = () => {
    const strengths = [];
    const weaknesses = [];
    const opportunities = [];
    const threats = [];
    const recommendations = [];

    // Strengths Analysis
    if (formData.experience && formData.experience !== 'Less than 1 year') {
      strengths.push('Professional teaching experience');
    }
    
    if (formData.certifications) {
      strengths.push('Professional certifications and qualifications');
    }
    
    if (formData.currentStudents && formData.currentStudents.active > 0) {
      strengths.push(`Established student base of ${formData.currentStudents.active} students`);
    }
    
    if (formData.uniqueValue) {
      strengths.push('Clear unique value proposition');
    }
    
    if (formData.marketingChannels && formData.marketingChannels.length > 0) {
      strengths.push(`Existing marketing channels: ${formData.marketingChannels.length} platforms`);
    }
    
    if (formData.teachingMethod) {
      strengths.push(`Specialized teaching method: ${formData.teachingMethod}`);
    }
    
    if (formData.subject) {
      strengths.push(`Expertise in ${formData.subject}`);
    }
    
    // New strength based on leads/prospects
    if (formData.estimatedLeads && formData.estimatedLeads !== 'Not sure' && formData.estimatedLeads !== '0-50') {
      strengths.push('Established audience base for marketing');
    }
    
    if (formData.estimatedProspects && formData.estimatedProspects !== 'Not sure' && formData.estimatedProspects !== '0-50') {
      strengths.push('Qualified leads pipeline for conversion');
    }

    // If no strengths identified, provide general ones
    if (strengths.length === 0) {
      strengths.push('Passion for education and student success');
      strengths.push('Knowledge in your subject area');
      strengths.push('Commitment to continuous learning');
    }

    // Weaknesses Analysis
    if (formData.marketingBudget === 'No budget' || formData.marketingBudget === '$0-$50') {
      weaknesses.push('Limited marketing budget for paid advertising');
    }
    
    if (formData.timeCommitment === '1-2 hours' || !formData.timeCommitment) {
      weaknesses.push('Limited time for marketing activities');
    }
    
    if (!formData.currentStudents || formData.currentStudents.active < 5) {
      weaknesses.push('Small current student base');
    }
    
    if (!formData.marketingChannels || formData.marketingChannels.length === 0) {
      weaknesses.push('Limited presence across marketing channels');
    }
    
    if (formData.competition) {
      weaknesses.push('Competitive market environment');
    }
    
    if (formData.location === 'Online') {
      weaknesses.push('Global competition in online education');
    }
    
    // New weaknesses based on leads/prospects
    if (!formData.estimatedLeads || formData.estimatedLeads === 'Not sure' || formData.estimatedLeads === '0-50') {
      weaknesses.push('Limited marketing reach and audience');
    }
    
    if (!formData.estimatedProspects || formData.estimatedProspects === 'Not sure' || formData.estimatedProspects === '0-50') {
      weaknesses.push('Limited qualified leads for conversion');
    }

    // If no weaknesses identified, provide general ones
    if (weaknesses.length === 0) {
      weaknesses.push('Room for improvement in digital marketing skills');
      weaknesses.push('Potential to expand student base further');
      weaknesses.push('Opportunities to diversify marketing strategies');
    }

    // Opportunities Analysis
    if (formData.targetAudience === 'Adult learners' || formData.targetAudience === 'Professionals') {
      opportunities.push('Growing demand for professional development and upskilling');
    }
    
    if (formData.teachingMethod === 'Online courses') {
      opportunities.push('Expanding online education market');
    }
    
    if (formData.subject.toLowerCase().includes('tech') || formData.subject.toLowerCase().includes('coding') || 
        formData.subject.toLowerCase().includes('digital')) {
      opportunities.push('High demand for technology and digital skills');
    }
    
    if (formData.subject.toLowerCase().includes('language')) {
      opportunities.push('Global demand for language learning');
    }
    
    if (formData.experience && formData.experience !== 'Less than 1 year') {
      opportunities.push('Opportunity to leverage experience into premium offerings');
    }
    
    if (formData.currentStudents && formData.currentStudents.notActive > 0) {
      opportunities.push('Re-engagement opportunities with past students');
    }
    
    // New opportunities based on leads/prospects
    if (formData.estimatedLeads && formData.estimatedLeads !== 'Not sure' && formData.estimatedLeads !== '0-50') {
      opportunities.push('Potential to convert leads into prospects and students');
    }
    
    if (formData.estimatedProspects && formData.estimatedProspects !== 'Not sure' && formData.estimatedProspects !== '0-50') {
      opportunities.push('High potential for conversion to paying students');
    }

    // If no opportunities identified, provide general ones
    if (opportunities.length === 0) {
      opportunities.push('Potential to expand to new student demographics');
      opportunities.push('Opportunities to create digital products or courses');
      opportunities.push('Possibility to partner with educational institutions');
    }

    // Threats Analysis
    if (formData.competition) {
      threats.push('Competitive market environment');
    }
    
    if (formData.location === 'Online') {
      threats.push('Global competition in online education');
    }
    
    if (formData.subject.toLowerCase().includes('tech') || formData.subject.toLowerCase().includes('coding')) {
      threats.push('Rapid technological changes requiring continuous adaptation');
    }
    
    if (!formData.marketingBudget || formData.marketingBudget === 'No budget') {
      threats.push('Limited ability to compete with well-funded competitors');
    }
    
    // New threats based on leads/prospects
    if (formData.estimatedLeads && formData.estimatedLeads !== 'Not sure' && 
        (formData.estimatedLeads === '0-50' || formData.estimatedLeads === '50-100')) {
      threats.push('Limited audience reach compared to competitors');
    }

    // If no threats identified, provide general ones
    if (threats.length === 0) {
      threats.push('Market changes that may affect demand for your services');
      threats.push('Economic factors that could impact student enrollment');
      threats.push('Technology shifts that may require new teaching methods');
    }

    // Recommendations based on analysis
    if (weaknesses.includes('Limited marketing budget for paid advertising') || 
        weaknesses.includes('Limited time for marketing activities')) {
      recommendations.push('Focus on free marketing channels like social media content and email marketing');
      recommendations.push('Create valuable content to attract students organically');
    }
    
    if (opportunities.includes('Expanding online education market')) {
      recommendations.push('Create and sell online courses to scale your teaching business');
      recommendations.push('Develop a content marketing strategy to establish authority');
    }
    
    if (strengths.includes('Professional teaching experience')) {
      recommendations.push('Leverage your experience to create premium offerings or workshops');
      recommendations.push('Request testimonials from satisfied students to build credibility');
    }
    
    if (weaknesses.includes('Small current student base')) {
      recommendations.push('Implement a referral program to encourage word-of-mouth marketing');
      recommendations.push('Create a lead magnet to capture potential student information');
    }
    
    if (opportunities.includes('Growing demand for professional development and upskilling')) {
      recommendations.push('Develop specialized programs for working professionals');
      recommendations.push('Partner with companies to provide employee training');
    }
    
    if (weaknesses.includes('Limited presence across marketing channels')) {
      recommendations.push('Establish a presence on 2-3 key social media platforms');
      recommendations.push('Create a simple website or blog to showcase your expertise');
    }
    
    if (opportunities.includes('High demand for technology and digital skills')) {
      recommendations.push('Stay updated with latest trends in your subject area');
      recommendations.push('Offer training in emerging technologies or methodologies');
    }
    
    if (threats.includes('Rapid technological changes requiring continuous adaptation')) {
      recommendations.push('Invest time in continuous professional development');
      recommendations.push('Join educator communities to stay informed about industry trends');
    }
    
    // New recommendations based on leads/prospects
    if (weaknesses.includes('Limited marketing reach and audience') || 
        weaknesses.includes('Limited qualified leads for conversion')) {
      recommendations.push('Create lead magnets to grow your email list and social media following');
      recommendations.push('Implement a content marketing strategy to attract and educate potential students');
    }
    
    if (opportunities.includes('Potential to convert leads into prospects and students')) {
      recommendations.push('Develop a lead nurturing sequence to convert leads to prospects');
      recommendations.push('Create a sales funnel to convert prospects to paying students');
    }
    
    if (threats.includes('Limited audience reach compared to competitors')) {
      recommendations.push('Invest in building your personal brand and online presence');
      recommendations.push('Focus on niche specialization to stand out from general competitors');
    }

    // General recommendations if none were generated
    if (recommendations.length === 0) {
      recommendations.push('Create a consistent content schedule to build your audience');
      recommendations.push('Develop a simple website to showcase your services and expertise');
      recommendations.push('Collect testimonials from satisfied students to build credibility');
      recommendations.push('Set aside dedicated time each week for marketing activities');
      recommendations.push('Experiment with different teaching formats to reach more students');
    }

    setAnalysis({
      strengths,
      weaknesses,
      opportunities,
      threats,
      recommendations
    });
  };

  useEffect(() => {
    generateSWOTAnalysis();
  }, [formData]);

  return (
    <div className="max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <SafeIcon icon={FiBarChart} className="text-2xl text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Marketing Analysis</h2>
          <p className="text-gray-600">SWOT analysis and personalized recommendations</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Strengths */}
          <div className="bg-green-50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiAward} className="text-green-600 text-xl mr-3" />
              <h3 className="font-bold text-green-800">Strengths</h3>
            </div>
            <ul className="space-y-2">
              {analysis.strengths.map((strength, index) => (
                <li key={index} className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-green-700">{strength}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Weaknesses */}
          <div className="bg-red-50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiTarget} className="text-red-600 text-xl mr-3" />
              <h3 className="font-bold text-red-800">Areas for Improvement</h3>
            </div>
            <ul className="space-y-2">
              {analysis.weaknesses.map((weakness, index) => (
                <li key={index} className="flex items-start">
                  <SafeIcon icon={FiTarget} className="text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-red-700">{weakness}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Opportunities */}
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiCompass} className="text-blue-600 text-xl mr-3" />
              <h3 className="font-bold text-blue-800">Opportunities</h3>
            </div>
            <ul className="space-y-2">
              {analysis.opportunities.map((opportunity, index) => (
                <li key={index} className="flex items-start">
                  <SafeIcon icon={FiCompass} className="text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-blue-700">{opportunity}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Threats */}
          <div className="bg-orange-50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiUsers} className="text-orange-600 text-xl mr-3" />
              <h3 className="font-bold text-orange-800">Market Challenges</h3>
            </div>
            <ul className="space-y-2">
              {analysis.threats.map((threat, index) => (
                <li key={index} className="flex items-start">
                  <SafeIcon icon={FiUsers} className="text-orange-600 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-orange-700">{threat}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Recommendations */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-8">
          <div className="flex items-center mb-4">
            <SafeIcon icon={FiTrendingUp} className="text-purple-600 text-xl mr-3" />
            <h3 className="font-bold text-purple-800">Personalized Recommendations</h3>
          </div>
          <ul className="space-y-3">
            {analysis.recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start bg-white p-3 rounded-lg">
                <SafeIcon icon={FiStar} className="text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
          >
            Previous
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNext}
            className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 shadow-lg hover:shadow-xl transition-all"
          >
            View Complete Plan
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default MarketingAnalysis;